"use client";

import { Suspense, useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { getTrafficInfo, toGA4StandardFormat } from "@/lib/utils/traffic-source";

/**
 * Google Analytics 페이지뷰 추적 컴포넌트 (내부)
 * useSearchParams()를 사용하므로 Suspense로 감싸져야 함
 */
function AnalyticsTracker() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // gtag 함수가 로드되었는지 확인
    if (typeof window === "undefined") return;

    // 시뮬레이션 상세페이지(/simulations/[id]), 입찰 페이지(/simulations/[id]/bid), 결과 페이지(/simulations/[id]/result)는 GA 추적에서 제외
    // 제외 로직을 가장 먼저 실행하여 확실히 차단
    // pathname을 정규화 (끝의 슬래시 제거)
    const normalizedPath = pathname.replace(/\/$/, '');
    
    console.log(`[GA Debug] 경로 체크 시작:`, {
      originalPath: pathname,
      normalizedPath: normalizedPath,
      startsWithSimulations: normalizedPath.startsWith('/simulations/'),
      isNotSimulationsRoot: normalizedPath !== '/simulations',
    });
    
    // /simulations/[id] 형식의 경로인지 확인 (UUID, 숫자, 하이픈 등 모든 형식 허용)
    // 상세: /simulations/[id]
    // 입찰: /simulations/[id]/bid  
    // 결과: /simulations/[id]/result
    const isDetailPage = normalizedPath.match(/^\/simulations\/[^\/]+$/);
    const isBidPage = normalizedPath.match(/^\/simulations\/[^\/]+\/bid$/);
    const isResultPage = normalizedPath.match(/^\/simulations\/[^\/]+\/result$/);
    
    const shouldExclude = normalizedPath.startsWith('/simulations/') && 
        normalizedPath !== '/simulations' &&
        (isDetailPage || isBidPage || isResultPage);
    
    console.log(`[GA Debug] 제외 로직 체크 결과:`, {
      pathname: pathname,
      normalizedPath: normalizedPath,
      isDetailPage: !!isDetailPage,
      isBidPage: !!isBidPage,
      isResultPage: !!isResultPage,
      shouldExclude: shouldExclude,
    });
    
    if (shouldExclude) {
      console.log(`🚫 [GA Debug] 시뮬레이션 상세/입찰/결과 페이지 추적 제외: ${pathname}`);
      console.log(`   - 상세 페이지: ${!!isDetailPage}`);
      console.log(`   - 입찰 페이지: ${!!isBidPage}`);
      console.log(`   - 결과 페이지: ${!!isResultPage}`);
      return; // 여기서 즉시 종료
    }

    const checkGtagLoaded = () => {
      return typeof window.gtag !== "undefined" || 
             (window.dataLayer && Array.isArray(window.dataLayer));
    };

    // gtag가 로드될 때까지 대기 (최대 5초)
    const maxWaitTime = 5000;
    const startTime = Date.now();
    
    const waitForGtag = (callback: () => void) => {
      if (checkGtagLoaded()) {
        callback();
        return;
      }
      
      if (Date.now() - startTime > maxWaitTime) {
        console.warn("[GA Debug] gtag 로드 타임아웃 - GA 스크립트가 로드되지 않았을 수 있습니다.");
        return;
      }
      
      setTimeout(() => waitForGtag(callback), 100);
    };

    waitForGtag(() => {
      // 제외 로직을 다시 한 번 확인 (이중 체크)
      const normalizedPathCheck = pathname.replace(/\/$/, '');
      const isDetailPageCheck = normalizedPathCheck.match(/^\/simulations\/[^\/]+$/);
      const isBidPageCheck = normalizedPathCheck.match(/^\/simulations\/[^\/]+\/bid$/);
      const isResultPageCheck = normalizedPathCheck.match(/^\/simulations\/[^\/]+\/result$/);
      
      const shouldExcludeCheck = normalizedPathCheck.startsWith('/simulations/') && 
          normalizedPathCheck !== '/simulations' &&
          (isDetailPageCheck || isBidPageCheck || isResultPageCheck);
      
      if (shouldExcludeCheck) {
        console.log(`🚫 [GA Debug] 시뮬레이션 상세/입찰/결과 페이지 추적 제외 (이중 체크): ${pathname}`);
        console.log(`   - 상세 페이지: ${!!isDetailPageCheck}`);
        console.log(`   - 입찰 페이지: ${!!isBidPageCheck}`);
        console.log(`   - 결과 페이지: ${!!isResultPageCheck}`);
        return;
      }

      const fullPath = pathname + (searchParams?.toString() ? `?${searchParams.toString()}` : "");
      
      console.group(`[GA Debug] 페이지뷰 추적 시작 - ${fullPath}`);
      console.log("경로:", pathname);
      console.log("쿼리 파라미터:", searchParams?.toString() || "없음");
      console.log("전체 경로:", fullPath);
      
      // dataLayer 상태 확인
      if (window.dataLayer) {
        console.log("dataLayer 상태:", {
          length: window.dataLayer.length,
          lastItems: window.dataLayer.slice(-3),
        });
      } else {
        console.warn("dataLayer가 존재하지 않습니다.");
      }

      // 유입 경로 정보 가져오기
      const { fbclid, trafficSource } = getTrafficInfo();
      
      // GA4 표준 형식으로 변환
      const ga4Standard = toGA4StandardFormat(trafficSource);
      
      console.log("[Page View] 유입 경로 정보:", {
        traffic_source: trafficSource.source,
        traffic_medium: trafficSource.medium,
        ga4_source: ga4Standard.source,
        ga4_medium: ga4Standard.medium,
        is_meta_ad: !!fbclid || trafficSource.isMeta,
      });

      // gtag 함수 확인
      if (typeof window.gtag === "function") {
        console.log("✅ gtag 함수 사용 가능");
        
        // 페이지뷰 추적 (유입 경로 정보 포함)
        // GA4 표준 파라미터(source, medium) 추가하여 채널 그룹이 올바르게 인식되도록 함
        const pageTitle = document.title || "Bidix - 경매 검토 보조도구";
        const configParams: Record<string, unknown> = {
          page_path: fullPath,
          page_title: pageTitle,
          page_location: window.location.href,
          // GA4 표준 파라미터 (채널 그룹 인식용)
          source: ga4Standard.source,
          medium: ga4Standard.medium,
          // 커스텀 파라미터 (상세 분석용)
          traffic_source: trafficSource.source,
          traffic_medium: trafficSource.medium,
          is_meta_ad: !!fbclid || trafficSource.isMeta,
        };
        
        // campaign이 있을 때만 추가 (빈 문자열 방지)
        if (trafficSource.campaign && trafficSource.campaign.trim() !== "") {
          configParams.campaign = trafficSource.campaign;
          configParams.traffic_campaign = trafficSource.campaign;
        }
        
        // fbclid가 있을 때만 추가
        if (fbclid) {
          configParams.fbclid = fbclid;
        }
        
        try {
          window.gtag("config", "G-XQRBB0F6V2", configParams);
          
          console.log("✅ gtag('config') 호출 완료 (유입 경로 정보 포함)");
          console.log("전송된 데이터:", configParams);
        } catch (error) {
          console.error("❌ gtag 호출 실패:", error);
        }
      } else if (window.dataLayer) {
        console.log("⚠️ gtag 함수 없음, dataLayer 직접 사용");
        
        // dataLayer에 직접 푸시 (유입 경로 정보 포함)
        // GA4 표준 파라미터(source, medium) 추가하여 채널 그룹이 올바르게 인식되도록 함
        const pageTitle = document.title || "Bidix - 경매 검토 보조도구";
        const pageViewData: Record<string, unknown> = {
          event: "page_view",
          page_path: fullPath,
          page_title: pageTitle,
          page_location: window.location.href,
          // GA4 표준 파라미터 (채널 그룹 인식용)
          source: ga4Standard.source,
          medium: ga4Standard.medium,
          // 커스텀 파라미터 (상세 분석용)
          traffic_source: trafficSource.source,
          traffic_medium: trafficSource.medium,
          is_meta_ad: !!fbclid || trafficSource.isMeta,
        };
        
        // campaign이 있을 때만 추가 (빈 문자열 방지)
        if (trafficSource.campaign && trafficSource.campaign.trim() !== "") {
          pageViewData.campaign = trafficSource.campaign;
          pageViewData.traffic_campaign = trafficSource.campaign;
        }
        
        // fbclid가 있을 때만 추가
        if (fbclid) {
          pageViewData.fbclid = fbclid;
        }
        
        try {
          window.dataLayer.push(pageViewData);
          
          console.log("✅ dataLayer.push() 호출 완료 (유입 경로 정보 포함)");
          console.log("전송된 데이터:", pageViewData);
        } catch (error) {
          console.error("❌ dataLayer.push() 실패:", error);
        }
      } else {
        console.error("❌ gtag와 dataLayer 모두 사용 불가능");
      }

      // 네트워크 요청 모니터링 (GA 요청 확인)
      const checkNetworkRequests = () => {
        if (typeof PerformanceObserver === "undefined") {
          console.log("⚠️ PerformanceObserver 사용 불가 - 네트워크 요청 모니터링 건너뜀");
          return;
        }

        try {
          const observer = new PerformanceObserver((list) => {
            const entries = list.getEntries();
            entries.forEach((entry) => {
              if (entry.name.includes("google-analytics") || 
                  entry.name.includes("gtag") ||
                  entry.name.includes("analytics") ||
                  entry.name.includes("collect")) {
                console.log("🌐 GA 네트워크 요청 감지:", {
                  url: entry.name,
                  type: entry.entryType,
                  duration: entry.duration,
                });
              }
            });
          });
          
          observer.observe({ entryTypes: ["resource", "navigation"] });
          
          // 3초 후 observer 해제
          setTimeout(() => {
            observer.disconnect();
          }, 3000);
        } catch (error) {
          console.warn("네트워크 모니터링 설정 실패:", error);
        }
      };

      // 약간의 지연 후 네트워크 요청 확인 (GA 요청이 발생할 시간을 줌)
      setTimeout(checkNetworkRequests, 500);

      // Meta Pixel 페이지뷰 추적 제거 (deprecated API 방지)
      // Meta Pixel은 필요한 페이지만 로드되며, PageView는 GA에서만 추적합니다.
      // 특정 이벤트(Lead, Purchase 등)는 해당 페이지에서 직접 trackMetaPixel 호출

      console.groupEnd();
    });
  }, [pathname, searchParams]);


  return null;
}

/**
 * Google Analytics 페이지뷰 추적 및 디버깅 Provider
 * 
 * Next.js App Router에서는 페이지 전환 시 자동으로 페이지뷰를 추적하지 않으므로,
 * 이 컴포넌트가 경로 변경을 감지하여 GA에 페이지뷰를 전송합니다.
 * 
 * 디버깅 기능:
 * - 페이지뷰 발생 시점 로그
 * - gtag 호출 여부 확인
 * - dataLayer 상태 확인
 * - 네트워크 요청 모니터링
 */
export function AnalyticsProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Suspense fallback={null}>
        <AnalyticsTracker />
      </Suspense>
      {children}
    </>
  );
}

// TypeScript 타입 확장
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}
